import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  skipToken,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "@/lib/http.ts";
import { isAxiosError } from "axios";
import type {
  AddContactHandleReq,
  Contact,
  ContactListSort,
  CreateContactHandleResp,
  Handle,
  UpdateContactReq,
} from "@/types/contact.ts";

const CONTACTS_PAGE_SIZE = 50;

interface ContactsPage {
  contacts: Contact[];
  total: number;
  myContacts?: Contact[];
}

export const contactQueries = {
  all: () => ["contacts"] as const,
  list: ({
    connectorId,
    hasProfile,
    // Opt-in, so only a surface that offers the sort control chooses the ordering.
    // The recipient picker browses this list with no control of its own, and the
    // ranked head costs a query it has no reason to pay on every page.
    // `DEFAULT_CONTACT_LIST_SORT` is the control's default selection, not this
    // query's.
    sort = "alphabetical",
  }: { connectorId?: number; hasProfile?: boolean; sort?: ContactListSort } = {}) =>
    infiniteQueryOptions({
      // `sort` is part of the key because the server orders and pages the list —
      // switching it is a different list, not a reordering of the loaded rows.
      queryKey: [...contactQueries.all(), "list", { connectorId, hasProfile, sort }] as const,
      queryFn: async ({ pageParam = 0, signal }) => {
        const params = new URLSearchParams();
        params.set("withMemories", "false");
        params.set("limit", CONTACTS_PAGE_SIZE.toString());
        params.set("page", pageParam.toString());
        params.set("sort", sort);
        // The endpoint filters on `connectorId`; this previously sent `accountId`,
        // which the handler ignores — so the account picker silently did nothing.
        if (connectorId) params.set("connectorId", connectorId.toString());
        if (hasProfile) params.set("hasProfile", "true");
        const response = await api.get<ContactsPage>(`/api/a/contacts?${params}`, { signal });
        return response.data;
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages, lastPageParam) => {
        // Stop once we've loaded every row the server reports. Comparing against
        // `total` (rather than a short-page heuristic) avoids one wasted empty
        // fetch when the final page happens to be exactly CONTACTS_PAGE_SIZE.
        const loaded = allPages.reduce((sum, page) => sum + page.contacts.length, 0);
        if (loaded >= lastPage.total) return undefined;
        return lastPageParam + 1;
      },
    }),
  search: ({
    query,
    connectorId,
    hasProfile,
  }: {
    query: string;
    connectorId?: number;
    hasProfile?: boolean;
  }) =>
    infiniteQueryOptions({
      queryKey: [...contactQueries.all(), "search", { query, connectorId, hasProfile }] as const,
      queryFn: async ({ pageParam = 0, signal }) => {
        const params = new URLSearchParams();
        params.set("withMemories", "false");
        params.set("query", query);
        params.set("limit", CONTACTS_PAGE_SIZE.toString());
        params.set("page", pageParam.toString());
        if (connectorId) params.set("connectorId", connectorId.toString());
        // Compose the "With Bio" filter with the query: the server restricts the
        // ranked results to profiled contacts, same as the browse list.
        if (hasProfile) params.set("hasProfile", "true");
        const response = await api.get<Contact[]>(`/api/a/contacts?${params}`, { signal });
        return response.data;
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        // Ranked search reports no total, so stop on the first short page rather
        // than comparing against a count. A full page means there may be more.
        if (lastPage.length < CONTACTS_PAGE_SIZE) return undefined;
        return lastPageParam + 1;
      },
    }),
  typeahead: ({ query, connectorId }: { query: string; connectorId?: number }) =>
    queryOptions({
      queryKey: [...contactQueries.all(), "typeahead", { query, connectorId }] as const,
      queryFn: async ({ signal }) => {
        const params = new URLSearchParams();
        params.set("query", query);
        params.set("limit", CONTACTS_PAGE_SIZE.toString());
        if (connectorId) params.set("connectorId", connectorId.toString());
        const response = await api.get<Contact[]>(`/api/a/contacts/typeahead?${params}`, {
          signal,
        });
        return response.data;
      },
      // Keep the prior results visible while the next keystroke's query loads, so
      // the dropdown narrows in place instead of flashing the broader local
      // fallback list on each (uncached) request.
      placeholderData: keepPreviousData,
    }),
  detail: (contactId: string | undefined) =>
    queryOptions({
      queryKey: [...contactQueries.all(), contactId] as const,
      queryFn: contactId
        ? async ({ signal }) => {
            const response = await api.get<Contact>(`/api/a/contacts/${contactId}`, { signal });
            return response.data;
          }
        : skipToken,
      // The contact page and its shared detail body mount separate observers.
      // Keep a recent result fresh so the second observer reuses it.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }),
  threadParticipants: (threadId: number | undefined) =>
    queryOptions({
      queryKey: [...contactQueries.all(), "threads", threadId, "participants"] as const,
      queryFn: threadId
        ? async ({ signal }) => {
            const response = await api.get<Contact[]>(`/api/a/threads/${threadId}/participants`, {
              signal,
            });
            return response.data;
          }
        : skipToken,
    }),
};

export const useContact = (contactId: string | undefined) => {
  const { data, isLoading, error } = useQuery(contactQueries.detail(contactId));
  return { contact: data, isLoading, error };
};

/**
 * Mutations for editing a single contact from the detail sidebar. Each
 * invalidates the whole `contacts` key on success, which covers both the list
 * and the `["contacts", contactId]` detail query (invalidation is prefix-based).
 */
export const useUpdateContact = (contactId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdateContactReq) => {
      const response = await api.post<Contact>(`/api/a/contacts/${contactId}/update`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactQueries.all() });
    },
  });
};

export const useAddContactHandle = (contactId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: AddContactHandleReq) => {
      const response = await api.post<Handle>(`/api/a/contacts/${contactId}/handles`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactQueries.all() });
    },
  });
};

export const useSetPreferredHandle = (contactId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (handleId: string) => {
      const response = await api.post<Handle>(
        `/api/a/contacts/${contactId}/handles/${handleId}/preferred`,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactQueries.all() });
    },
  });
};

export const useDeleteContactHandle = (contactId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (handleId: string) => {
      await api.post(`/api/a/contacts/${contactId}/handles/${handleId}/delete`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactQueries.all() });
    },
  });
};

/**
 * Delete a contact. Irreversible.
 *
 * Removes the contact's own queries rather than invalidating them: invalidating
 * would refetch a contact the server no longer has and cache the 404 as an error
 * state, which the detail body would render on its way out.
 *
 * A 404 resolves rather than rejects: the contact is already gone (deleted from
 * another tab or device), which is the outcome asked for, and the cache still
 * needs the same cleanup.
 */
export const useDeleteContact = (contactId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      try {
        await api.post(`/api/a/contacts/${contactId}/delete`);
      } catch (error) {
        if (!isAxiosError(error) || error.response?.status !== 404) throw error;
      }
    },
    onSuccess: () => {
      const detailKey = contactQueries.detail(contactId).queryKey;
      // Cancel before removing: a GET issued before the delete may still be in
      // flight, and its response would repopulate the entry when it lands.
      void queryClient.cancelQueries({ queryKey: detailKey });
      queryClient.removeQueries({ queryKey: detailKey });
      // The activity tabs key on the contact outside the `contacts` prefix.
      queryClient.removeQueries({ queryKey: ["contactActivity", contactId] });
      // Lists and searches still hold the row. The detail key shares the prefix,
      // so it is excluded explicitly or this would recreate what was just removed.
      queryClient.invalidateQueries({
        queryKey: contactQueries.all(),
        predicate: (query) => query.queryKey[1] !== contactId,
      });
    },
  });
};

export const useThreadParticipants = (threadId: number | undefined) => {
  const { data, isLoading, error } = useQuery(contactQueries.threadParticipants(threadId));
  return { participants: data ?? [], isLoading, error };
};

/**
 * Drop contacts already present in an earlier page, keeping the first occurrence.
 *
 * Pages are cut by offset from an ordering the server rebuilds per request, so a
 * contact whose position changes between two requests — renamed, or newly
 * interacted with — can straddle the boundary and arrive on both pages. Keeping
 * the first occurrence holds the row where the user already saw it rather than
 * moving it down as they scroll. This can't recover a contact the same shift
 * pushed *past* the boundary; only cursor paging would, and both sorts have that
 * gap equally.
 */
export const dedupeContactsById = (contacts: Contact[]): Contact[] => {
  const seen = new Set<string>();
  return contacts.filter((contact) => {
    if (seen.has(contact.id)) return false;
    seen.add(contact.id);
    return true;
  });
};

export const useContacts = ({
  enabled = true,
  connectorId,
  hasProfile,
  sort,
}: {
  enabled?: boolean;
  connectorId?: number;
  hasProfile?: boolean;
  sort?: ContactListSort;
} = {}) => {
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      ...contactQueries.list({ connectorId, hasProfile, sort }),
      enabled,
    });
  // Deduped across pages, not within one: `getNextPageParam` still measures the
  // raw page length, so this can't stall paging by shortening a full page.
  const contacts = dedupeContactsById(data?.pages?.flatMap((p) => p.contacts) ?? []);
  const total = data?.pages?.[0]?.total ?? 0;
  const myContacts = data?.pages?.[0]?.myContacts ?? [];
  return {
    contacts,
    total,
    myContacts,
    isLoading: enabled && isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

export const useContactSearch = (query: string, connectorId?: number, hasProfile?: boolean) => {
  const enabled = query.trim().length > 0;
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      ...contactQueries.search({ query, connectorId, hasProfile }),
      enabled,
    });
  // Dedupe across page boundaries: growing the ranked retrieval window for a
  // later page can re-surface a row already shown, and duplicate ids break keys.
  const contacts = dedupeContactsById(data?.pages?.flat() ?? []);
  return {
    contacts,
    isLoading: enabled && isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

/** Keep the first occurrence of each contact id, preserving ranked order. */
const _dedupeContactsById = (contacts: Contact[]): Contact[] => {
  const seen = new Set<string>();
  return contacts.filter((c) => {
    if (seen.has(c.id)) return false;
    seen.add(c.id);
    return true;
  });
};

/**
 * Fast prefix-based contact search for typeahead/autocomplete surfaces. Hits the
 * dedicated typeahead endpoint, which prefix-matches with a stripped-down pipeline
 * and falls back to full contact search on a total miss so recall isn't regressed.
 */
export const useContactTypeahead = (query: string, connectorId?: number) => {
  const enabled = query.trim().length > 0;
  const { data, isLoading, isPlaceholderData, error } = useQuery({
    ...contactQueries.typeahead({ query, connectorId }),
    enabled,
  });
  return {
    contacts: data ?? [],
    isLoading: enabled && isLoading,
    // True when `contacts` are a prior query's results held by keepPreviousData
    // while the current query is still loading (i.e. stale).
    isStale: isPlaceholderData,
    error,
  };
};

/**
 * Create a contact + handle from a free-text value (e.g. email or phone).
 * Returns a Contact with the newly created handle attached.
 */
export const createContactWithHandle = async ({
  contactName = "",
  handleType,
  handleValue,
}: {
  contactName?: string;
  handleType: "emailAddress" | "phoneNumber" | "address";
  handleValue: string;
}): Promise<Contact> => {
  const response = await api.post<CreateContactHandleResp>("/api/a/contacts/handles/new", {
    contactName,
    handleType,
    handleValue,
  });
  const handle = response.data;
  if (!handle.contact) {
    throw new Error("Failed to create contact");
  }
  return {
    id: handle.contact.id,
    name: handle.contact.name,
    type: handle.contact.type,
    connectorId: handle.contact.connectorId,
    handles: [handle],
  };
};
